import { useState, useRef, useEffect, type RefObject } from "react";
import { CustomCellEditorProps } from "ag-grid-react";

import {
  AgeReductionEntry,
  sortEntriesByAge,
  isEntryCompletelyEmpty,
  ageReductionScheduleSchema,
  isFieldValid,
} from "./ageReductionUtils";

import type { PlanEditorRowData } from "../../types/values.types";

interface EditableWhen {
  dependsOn: string;
  disabledValues: unknown[];
}

interface ColDefWithEditableWhen {
  editableWhen?: EditableWhen;
}

interface EntryWithoutId {
  reductionPercent: number | string;
  age: number | string;
}

interface UseAgeReductionEditorResult {
  entries: AgeReductionEntry[];
  open: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  isDisabled: boolean;
  handleClose: () => void;
  handleDone: () => void;
  handleAddEntry: () => void;
  handleRemoveEntry: (index: number) => void;
  handleFieldChange: (
    index: number,
    fieldKey: "reductionPercent" | "age",
    fieldValue: string,
  ) => void;
  handleFieldBlur: (index: number, fieldKey: string) => void;
  shouldShowFieldError: (
    index: number,
    fieldKey: "reductionPercent" | "age",
    value: number | string,
  ) => boolean;
}

export const useAgeReductionEditor = (
  props: CustomCellEditorProps<PlanEditorRowData, unknown>,
): UseAgeReductionEditorResult => {
  const { stopEditing, api, node, column } = props;
  const value: unknown =
    typeof props.value === "string" && props.value.length
      ? JSON.parse(props.value)
      : props.value;

  // Counter for generating unique IDs
  const idCounterRef = useRef(0);
  const getNextId = (): string => `entry-${idCounterRef.current++}`;

  // Check if field is disabled based on editableWhen conditions
  const colDef = column?.getColDef() as ColDefWithEditableWhen | undefined;
  const editableWhen = colDef?.editableWhen;
  let isDisabled = false;
  if (editableWhen && node?.data) {
    const dependsOnField = editableWhen.dependsOn;
    const disabledValues = editableWhen.disabledValues || [];
    const dependsOnValue = node.data[dependsOnField];
    isDisabled = disabledValues.includes(dependsOnValue);
  }

  // If disabled, immediately stop editing and don't open popover
  useEffect(() => {
    if (isDisabled && stopEditing) {
      stopEditing();
    }
  }, [isDisabled, stopEditing]);

  // Initialize entries from value or use default
  const initialEntries = sortEntriesByAge(
    Array.isArray(value) && value.length > 0
      ? value.map((entry: EntryWithoutId) => ({
          ...entry,
          id: getNextId(),
        }))
      : [{ id: getNextId(), reductionPercent: "", age: "" }],
  );
  const [entries, setEntries] = useState<AgeReductionEntry[]>(initialEntries);

  // Track which fields have been blurred (touched)
  const [touchedFields, setTouchedFields] = useState<
    Set<string> // format: "index-fieldKey"
  >(new Set());

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Auto-open popover when editor is created (only if not disabled)
  useEffect(() => {
    if (!isDisabled) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDisabled]);

  /**
   * Check if field has been touched (blurred)
   */
  const isFieldTouched = (index: number, fieldKey: string): boolean =>
    touchedFields.has(`${index}-${fieldKey}`);

  /**
   * Check if field should show error (touched and invalid)
   */
  const shouldShowFieldError = (
    index: number,
    fieldKey: "reductionPercent" | "age",
    value: number | string,
  ): boolean =>
    isFieldTouched(index, fieldKey) && !isFieldValid(value, fieldKey);

  const handleClose = (): void => {
    setOpen(false);
    if (stopEditing) {
      setTimeout(() => {
        stopEditing();
      }, 0);
    }
  };

  const handleDone = (): void => {
    // Filter out completely empty entries
    const filteredEntries = entries.filter((e) => !isEntryCompletelyEmpty(e));

    // Sort entries by age before saving
    const sortedEntries = sortEntriesByAge(filteredEntries);

    // Strip out IDs before saving
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const entriesWithoutIds = sortedEntries.map(({ id, ...rest }) => rest);

    // Validate the filtered entries
    try {
      ageReductionScheduleSchema.parse(entriesWithoutIds);
    } catch {
      // Validation failed - should not happen if Done button logic is correct
      return;
    }

    // Set the data value directly on the node
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, entriesWithoutIds);
    }

    // Stop editing and refresh
    if (stopEditing) {
      stopEditing();
    }

    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleAddEntry = (): void => {
    const maxEntries = 10;
    if (entries.length >= maxEntries) {
      return;
    }

    const newEntry: AgeReductionEntry = {
      id: getNextId(),
      reductionPercent: "",
      age: "",
    };

    const updatedEntries = sortEntriesByAge([...entries, newEntry]);
    setEntries(updatedEntries);
  };

  const handleRemoveEntry = (index: number): void => {
    // Don't allow removing the last entry
    if (entries.length <= 1) {
      return;
    }

    const updatedEntries = sortEntriesByAge(
      entries.filter((_, i) => i !== index),
    );
    setEntries(updatedEntries);

    // Remove touched state for this entry
    const newTouchedFields = new Set(touchedFields);
    newTouchedFields.delete(`${index}-reductionPercent`);
    newTouchedFields.delete(`${index}-age`);
    setTouchedFields(newTouchedFields);
  };

  const handleFieldChange = (
    index: number,
    fieldKey: "reductionPercent" | "age",
    fieldValue: string,
  ): void => {
    const updatedEntries = [...entries];

    // Convert to number if valid, otherwise keep as string
    let processedValue: number | string = fieldValue;
    if (fieldValue !== "") {
      const numValue = parseFloat(fieldValue);
      if (!isNaN(numValue)) {
        processedValue = numValue;
      }
    }

    updatedEntries[index] = {
      ...updatedEntries[index],
      [fieldKey]: processedValue,
    };

    // Don't sort during editing - only sort on add or submit
    setEntries(updatedEntries);
  };

  const handleFieldBlur = (index: number, fieldKey: string): void => {
    setTouchedFields((prev) => new Set(prev).add(`${index}-${fieldKey}`));
  };

  return {
    entries,
    open,
    anchorRef,
    isDisabled,
    handleClose,
    handleDone,
    handleAddEntry,
    handleRemoveEntry,
    handleFieldChange,
    handleFieldBlur,
    shouldShowFieldError,
  };
};
